/**
 * Database types — enums and interfaces shared between client and server.
 *
 * This is the SINGLE SOURCE OF TRUTH for all database enums and object shapes.
 * Both client (schema.ts) and server (ServerDexie.ts) import from here.
 */

import type Dexie from "dexie"
import type { Table } from "dexie"

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum ObjectType {
  Checklist = -2,
  Subtask = -1,
  Task = 0,
  Section = 1,
  Project = 2,
  Area = 3,
  List = 4,
  ContentBlock = 5,
  Root = 99,
}

export enum ObjectStatus {
  Hidden = -2,
  Canceled = -1,
  Unfinished = 0,
  Completed = 1,
}

export enum StartAtType {
  Default = 1,
  Someday = 2,
}

export enum StartAtSubType {
  Default = 1,
  ThisEvening = 2,
}

export enum ChecklistSubType {
  Item = 0,
  Heading = 1,
}

export enum ReminderKind {
  Reminder = "reminder",
  Alarm = "alarm",
}

export enum SourceType {
  Internal = "internal",
}

export enum TriggerType {
  Time = "time",
}

// ---------------------------------------------------------------------------
// Interfaces — matching Dexie Cloud schema v80
// ---------------------------------------------------------------------------

export interface DbObject {
  id?: string
  type: ObjectType
  title: string
  note?: string
  parentPath: string
  index: number
  status: ObjectStatus
  statusAt?: string
  statusBy?: string
  userStatus?: number | string
  progress?: number
  tags: string[]
  dueAt?: string
  isDeleted: 0 | 1
  deletedAt?: string
  deletedBy?: string
  owner?: string
  realmId?: string
  createdAt: string
  createdBy: string
  updatedAt?: string
  updatedBy?: string
  subType?: number
  /** Custom icon — prefix-routed: "pika:category/name" | "emoji:☕" | undefined (default TimelineIcon) */
  icon?: string
  doc?: unknown
  docHtml?: string
  fullTextIndex: string[]

  // ─── PLAN-RELATED FIELDS (all optional, backward-compatible) ───

  /** Points to the anchor task. Null/undefined = regular task, not part of a plan. */
  anchorTaskId?: string
  /** Fractional index for sorting within the plan view. Spacing: (i+1)*1000. */
  anchorTaskIndex?: number
  /** Which plan instance created this task. Null = regular task. */
  planId?: string

  // ─── RECURRENCE FIELDS (all optional, backward-compatible) ───

  /** Points back to the template task (= recurrence.id) */
  recurringTemplateId?: string
  /** Dedup key: "20260429" (FIXED) or "seq:7" (AFTER_COMPLETION) */
  occurrenceKey?: string
  /** Flag: this task is a generated recurring instance */
  isRecurrenceInstance?: 0 | 1
  /** Flag: this task IS the recurring template (filtered from date views) */
  isRecurringTemplate?: 0 | 1
  /** Outcome of a recurring instance (for streak/history) */
  instanceOutcome?: "completed" | "skipped" | "deferred" | "expired"
  /** Optional reason for skip (e.g. "sick", "vacation") */
  skipReason?: string
  /** Progressive overload: target value for this instance (e.g. 4.5 km) */
  progressiveTarget?: number

  // ─── AI ENRICHMENT (Fas 6) ───

  /** AI enrichment state: pending (awaiting AI), done, or failed */
  aiState?: "pending" | "done" | "failed"
  /** When AI enrichment was completed (UTC ms string) */
  aiGeneratedAt?: string
  /** Tokens consumed by AI enrichment for this instance */
  aiTokensUsed?: number

  // ─── TIME TRACKING ───

  /** Timer: when the task timer was started (UTC ms string). Null = not running. */
  timerStartedAt?: string
  /** Total accumulated time spent on this task (ms). Updated when timer stops or manual log. */
  timeSpentMs?: number
  /** JSON-serialized TimeEntry[] for detailed time log */
  timeEntries?: string

  // ─── ROUTINE CHAINS ───

  /** Which routine this instance belongs to */
  routineId?: string
  /** Order within the routine (0-based) */
  routineOrder?: number
  /** Whether this task is locked (waiting for previous in chain) */
  routineLocked?: 0 | 1

  // ─── CHECKLIST INDENT ───

  /** Indent depth for checklist items (0 = no indent, max 3). Not indexed. */
  depth?: number
}

export interface DbIndex {
  id?: string
  index: number
}

export interface DbStartAtIndex {
  id?: string
  index: number
  startAt: string
  type: StartAtType
  subType: StartAtSubType
  reminder?: string
}

export interface DbSetting {
  id?: string
  value: string
  device: string
  createdAt: string
  createdBy: string
  updatedAt?: string
  updatedBy?: string
}

export interface DbReminder {
  id: string
  taskId: string
  parentPath: string
  owner: string
  title?: string
  body?: string
  link?: string
  realmId?: string
  sourceType: SourceType | string
  triggerType: TriggerType | string
  fireAt?: string
  kind: ReminderKind | string
  createdAt: string
  createdBy: string
  updatedAt?: string
  updatedBy?: string
  isDeleted?: 0 | 1
  deletedAt?: string
}

export interface DbFile {
  id?: string
  name: string
  mimeType: string
  size: number
  content: Uint8Array
  thumbnail?: Uint8Array
  parentId?: string
  parentType?: "task" | "project" | "note" | "checklist"
  owner?: string
  realmId?: string
  createdAt: string
  createdBy: string
}

export interface DbSharedViewPref {
  id: string                    // "#" + containerId OR "#" + taskId
  scope: "container" | "task"
  showInDateViews: 0 | 1
  enableReminders: 0 | 1
  reminderOffsetMs?: number     // Custom offset (default: -3600000 = 1h before startAt)
  owner?: string                // :owner-scoped — invisible to others
  realmId?: string
}

// ---------------------------------------------------------------------------
// Recurrence — recurring task configuration (1:1 with template task)
// ---------------------------------------------------------------------------

export interface DbRecurrence {
  id: string                       // Same as template task's ID (1:1)

  // --- Core ---
  rrule?: string                   // RFC 5545 RRULE (FIXED mode only, undefined for AFTER)
  timezone: string                 // IANA timezone (e.g. "Europe/Stockholm")
  mode: "FIXED" | "AFTER_COMPLETION"
  startAt: string                  // UTC ms string — RRULE DTSTART anchor
  version: number                  // Bumped on template edit → detects stale instances

  // --- After-completion specific ---
  afterCompletionDays?: number     // Days after completion to generate next (mode=AFTER only)
  afterCompletionTime?: string     // Optional: "09:00" — set startAt to this time on target day

  // --- Per-day time override (FIXED weekly only) ---
  weekdayTimes?: string            // JSON: Record<number, string> e.g. {"0":"07:00","4":"09:00"}

  // --- Pile-up ---
  pilePolicy: "ALLOW" | "NO_PILE" | "CAP"
  pileCap?: number                 // Max open instances (when policy=CAP)

  // --- Limits ---
  endDate?: string                 // UTC ms — stop generating after this
  maxCount?: number                // Stop after N total instances
  excludeDates?: string[]          // ISO dates to skip: ["2025-12-25", "2025-12-26"]

  // --- Deadline (Things 3 "Add Deadline" model) ---
  deadlineOffset?: string          // Offset DSL: "+0d" (same as start), "+10d", "+3d>wd"
  showInTodayDaysBefore?: number   // Days before deadline to show in Today (default: 0)

  // --- Lookahead ---
  lookaheadDays: number            // Default: 30

  // --- Reminder ---
  reminderOffsetMs?: number        // e.g. -3600000 (1h before startAt) — LEGACY single
  reminderKind?: "reminder" | "alarm" // LEGACY single kind
  reminderOffsets?: string          // JSON: Array<{ offsetMs: number; kind: "reminder" | "alarm" }>

  // --- Instance placement ---
  insertPosition?: "top" | "bottom"
  targetContainerId?: string       // If set, instances are placed here instead of template's parentPath

  // --- AI (schema prepared, activated in Fas 6) ---
  aiEnabled?: 0 | 1
  aiPrompt?: string                // Max 500 chars
  aiCreativity?: string            // "focused" | "creative" | "wild"
  aiAvoidRepeat?: 0 | 1            // Send previous titles as context
  aiDetailLevel?: string           // "short" | "normal" | "detailed"

  // --- State ---
  lastGeneratedAt?: string         // UTC ms — last time generation ran
  pausedAt?: string                // UTC ms — if set, generation is paused
  resumeAt?: string                // UTC ms — auto-resume date (optional)
  pauseReason?: string             // "vacation", "injury", "break"
  instanceCount: number            // Running total of generated instances

  // --- Flexible window (goal-based) ---
  flexibleCount?: number           // e.g. 3 (times per period)
  flexiblePeriod?: "week" | "month"
  flexibleResetDay?: number        // 0=Monday, 1=Tuesday, ... (week start)
  flexibleCompletedThisPeriod?: number  // Running counter within current period
  flexiblePeriodStart?: string     // UTC ms — start of current period

  // --- Progressive overload ---
  progressiveField?: string        // "distance", "duration", "reps", "weight", custom
  progressiveStart?: number        // Starting value (e.g. 3)
  progressiveIncrement?: number    // Increase per period (e.g. 0.5)
  progressiveUnit?: string         // "km", "min", "reps", "kg"
  progressiveMax?: number          // Cap value (e.g. 10)
  progressivePer?: "instance" | "week" | "month"  // Increment frequency
  progressiveCurrent?: number      // Current target value

  // --- Focus ---
  autoFocus?: 0 | 1                // When set, new instances are auto-added to In Focus

  // --- Streak cache (updated on instance completion) ---
  currentStreak?: number
  longestStreak?: number
  lastCompletedAt?: string

  // --- Dexie Cloud ---
  owner?: string
  realmId?: string
  isDeleted: 0 | 1
  createdAt: string
  createdBy: string
  updatedAt?: string
  updatedBy?: string
}

// ---------------------------------------------------------------------------
// DbRoutine — ordered chain of recurring templates
// ---------------------------------------------------------------------------

export interface DbRoutine {
  id?: string
  title: string
  /** Time category for grouping */
  timeOfDay?: "morning" | "evening" | "anytime"
  /** Scheduled start time (e.g. "07:00") */
  scheduledTime?: string
  /** Ordered list of recurring template IDs */
  templateIds: string[]
  /** If true, each step must be completed before the next unlocks */
  strictOrder: boolean
  /** Custom icon */
  icon?: string

  // --- Dexie Cloud ---
  owner?: string
  realmId?: string
  isDeleted: 0 | 1
  createdAt: string
  createdBy: string
  updatedAt?: string
  updatedBy?: string
}

// ---------------------------------------------------------------------------
// TotodoDb — interface satisfied by both client and server Dexie instances
// ---------------------------------------------------------------------------

export type TotodoDb = Dexie & {
  totodo: Table<DbObject, string>
  startAtIndex: Table<DbStartAtIndex, string>
  privateIndex: Table<DbIndex, string>
  reminder: Table<DbReminder, string>
  setting: Table<DbSetting, string>
  files: Table<DbFile, string>
  plan: Table<import("./planTypes.js").DbPlanEntry, string>
  chatConversation: Table<import("./chatTypes.js").DbChatConversation, string>
  chatMessage: Table<import("./chatTypes.js").DbChatMessage, string>
  sharedViewPref: Table<DbSharedViewPref, string>
  recurrence: Table<DbRecurrence, string>
  routine: Table<DbRoutine, string>
  cloud: { currentUserId: string }
}
